import React, { useState } from 'react';
import {
  Search,
  SlidersHorizontal,
  X,
  ArrowDownWideNarrow,
} from 'lucide-react';
import { AccountsPager } from './AccountsPager';
import {
  AccountFilters,
  NO_CATEGORY,
  SORT_LABELS,
  countActiveFilters,
  emptyAccountFilters,
} from '../../utils/accountFilters';

/**
 * Sorting, shortlisting and paging, in one bar, above the account list.
 *
 * Two things this replaces:
 *
 *  - The separate Shortlist menu. Shortlisting by rating, amount, name, agent
 *    or account number now happens where the accounts are, so you no longer
 *    jump to another screen and back to narrow a list.
 *
 *  - Search-as-you-type. Every keystroke used to re-filter the whole book.
 *    Nothing happens now until Search is pressed — which is your point about
 *    reads, and it also stops the list jumping about while you are still
 *    typing a name.
 */

export interface AccountsFilterBarProps {
  value: AccountFilters;
  onChange: (next: AccountFilters) => void;
  /** Applied only when Search is pressed, so typing costs nothing. */
  onApply: (next: AccountFilters) => void;
  page: number;
  pageCount: number;
  total: number;
  from: number;
  to: number;
  onPage: (page: number) => void;
  statusOptions?: string[];
  branchOptions?: string[];
  zoneOptions?: string[];
  /**
   * The products actually present in the list being filtered, from
   * `productOptionsOf`. Not a fixed list of loan categories: the filter has to
   * offer the products the bank's own sheet contains, or it offers products
   * nobody holds and hides the ones everybody does.
   */
  productOptions?: string[];
  /** Account categories present in the list, from `accountCategoryOptionsOf`. */
  accountCategoryOptions?: string[];
  agentOptions?: Array<{ id: string; name: string }>;
  /** Hidden for an agent, who has only their own accounts. */
  showAgentFilter?: boolean;
}

export const AccountsFilterBar: React.FC<AccountsFilterBarProps> = ({
  value,
  onChange,
  onApply,
  page,
  pageCount,
  total,
  from,
  to,
  onPage,
  statusOptions = [],
  branchOptions = [],
  zoneOptions = [],
  productOptions = [],
  accountCategoryOptions = [],
  agentOptions = [],
  showAgentFilter = true,
}) => {
  const [open, setOpen] = useState(false);
  // What has been typed but not yet searched for. Keeping it separate is what
  // makes the Search button mean something.
  const [draft, setDraft] = useState<AccountFilters>(value);

  const activeCount = countActiveFilters(value);

  const fieldCls =
    'w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none';

  const apply = () => {
    onChange(draft);
    onApply(draft);
    onPage(1);
  };

  const clearAll = () => {
    const next = { ...emptyAccountFilters, sort: draft.sort };
    setDraft(next);
    onChange(next);
    onApply(next);
    onPage(1);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-xs text-xs">
      {/* ---------------- line 1: search + sort + page ---------------- */}
      <div className="p-3 flex flex-wrap items-center gap-2">
        {/* The search box gets the room it needs. It used to be squeezed
            between two other controls, too narrow to read what was typed. */}
        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={draft.text}
              onChange={(e) => setDraft({ ...draft, text: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  apply();
                }
              }}
              placeholder="Customer name, account number, customer code or mobile"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-8 py-2.5 text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            {draft.text && (
              <button
                type="button"
                onClick={() => setDraft({ ...draft, text: '' })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
                title="Clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={apply}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-sm transition shrink-0"
          >
            Search
          </button>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <ArrowDownWideNarrow className="w-4 h-4 text-slate-400" />
          <select
            value={draft.sort}
            onChange={(e) => {
              const next = { ...draft, sort: e.target.value as AccountFilters['sort'] };
              setDraft(next);
              // Sorting is free — it reorders what is already in memory — so it
              // applies at once rather than waiting for Search.
              onChange(next);
              onApply(next);
            }}
            className="bg-white border border-slate-200 rounded-xl px-2.5 py-2 font-semibold text-slate-800 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            {SORT_LABELS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold border transition shrink-0 ${
            activeCount > 0
              ? 'bg-blue-50 text-blue-700 border-blue-200'
              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
          }`}
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          <span>Shortlist</span>
          {activeCount > 0 && (
            <span className="px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-[9px]">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {/* ---------------- line 2: the shortlist panel ---------------- */}
      {open && (
        <div className="px-3 pb-3 border-t border-slate-100 pt-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-slate-700 font-bold mb-1">
                Outstanding from (₹)
              </label>
              <input
                inputMode="numeric"
                value={draft.minAmount}
                onChange={(e) => setDraft({ ...draft, minAmount: e.target.value })}
                placeholder="e.g. 100000"
                className={`${fieldCls} font-mono`}
              />
            </div>
            <div>
              <label className="block text-slate-700 font-bold mb-1">
                Outstanding to (₹)
              </label>
              <input
                inputMode="numeric"
                value={draft.maxAmount}
                onChange={(e) => setDraft({ ...draft, maxAmount: e.target.value })}
                placeholder="e.g. 5000000"
                className={`${fieldCls} font-mono`}
              />
            </div>
            <div>
              <label className="block text-slate-700 font-bold mb-1">
                Customer rating
              </label>
              <select
                value={draft.rating}
                onChange={(e) => setDraft({ ...draft, rating: e.target.value })}
                className={fieldCls}
              >
                <option value="">Any rating</option>
                <option value="PRIME">Prime only (4–5 stars)</option>
                {[5, 4, 3, 2, 1].map((r) => (
                  <option key={`fr-${r}`} value={String(r)}>
                    {'★'.repeat(r)} {r} star{r === 1 ? '' : 's'}
                  </option>
                ))}
                <option value="0">Unrated</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-700 font-bold mb-1">
                Account status
              </label>
              <select
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                className={fieldCls}
              >
                <option value="">Any status</option>
                {statusOptions.map((st) => (
                  <option key={`fs-${st}`} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            {zoneOptions.length > 1 && (
              <div>
                <label className="block text-slate-700 font-bold mb-1">Zone</label>
                <select
                  value={draft.zone}
                  onChange={(e) => setDraft({ ...draft, zone: e.target.value })}
                  className={fieldCls}
                >
                  <option value="">All zones</option>
                  {zoneOptions.map((z) => (
                    <option key={`fz-${z}`} value={z}>
                      {z}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {branchOptions.length > 1 && (
              <div>
                <label className="block text-slate-700 font-bold mb-1">Branch</label>
                <select
                  value={draft.branch}
                  onChange={(e) => setDraft({ ...draft, branch: e.target.value })}
                  className={fieldCls}
                >
                  <option value="">All branches</option>
                  {branchOptions.map((b) => (
                    <option key={`fb-${b}`} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {productOptions.length > 1 && (
              <div>
                <label className="block text-slate-700 font-bold mb-1">
                  Product (PROD_DESC)
                </label>
                <select
                  value={draft.product}
                  onChange={(e) => setDraft({ ...draft, product: e.target.value })}
                  className={fieldCls}
                >
                  <option value="">All products</option>
                  {productOptions.map((pr) => (
                    <option key={`fp-${pr}`} value={pr}>
                      {pr}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Always shown: "No category (blank)" is the way to find accounts
                still waiting for their first upload, so it must not hide
                itself when no account has a category yet. */}
            {(
              <div>
                <label className="block text-slate-700 font-bold mb-1">
                  Account category (Excel)
                </label>
                <select
                  value={draft.accountCategory}
                  onChange={(e) => setDraft({ ...draft, accountCategory: e.target.value })}
                  className={fieldCls}
                >
                  <option value="">All categories</option>
                  <option value={NO_CATEGORY}>No category (blank)</option>
                  {accountCategoryOptions.map((c) => (
                    <option key={`fc-${c}`} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {showAgentFilter && agentOptions.length > 0 && (
              <div>
                <label className="block text-slate-700 font-bold mb-1">Agent</label>
                <select
                  value={draft.agent}
                  onChange={(e) => setDraft({ ...draft, agent: e.target.value })}
                  className={fieldCls}
                >
                  <option value="">Any agent</option>
                  <option value="unassigned">Unassigned</option>
                  {agentOptions.map((a) => (
                    <option key={`fa-${a.id}`} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={apply}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-sm transition"
            >
              Apply shortlist
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition"
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* ---------------- line 3: paging ---------------- */}
      {/* The strip used to be written out here by hand. The agent portal had
          no equivalent at all, which is how its table ended up paging
          internally with no way to change the page. One component now, used
          by both, so they cannot drift apart again. */}
      <AccountsPager
        page={page}
        pageCount={pageCount}
        total={total}
        from={from}
        to={to}
        onPage={onPage}
        className="rounded-b-2xl"
      />
    </div>
  );
};

export default AccountsFilterBar;
